import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    useGetNirovisionAccessListQuery,
    useGetNirovisionAccessRequestsQuery,
    useProvideNirovisionAccessMutation,
    useRemoveNirovisionAccessMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../shared/AuthRoles';
import { theme } from '../../../../shared/theme';
import { handleHttpError } from '../../../../shared/utils';
import toast from '../../../shared/Toast';
import NirovisionAccessRequestsTab from '../../Nirovision/NirovisionAccessRequestsTab';
import {
    canRespondToNirovisionAccessRequests,
    canViewNirovisionAccessRequests,
} from '../../Nirovision/nirovisionAccessRequestUtils';
import NirovisionAccessTable from '../../Nirovision/NirovisionAccessTable';

type AccessResult = { ok: true } | { ok: false; error: string };
type TabKey = 'without' | 'with' | 'requests';

export default function NirovisonAccessModal({
    onClose,
    open,
    initialTab,
    bookingId,
    bookerType,
    removeAccessChecked,
    reservationStartDate,
    reservationEndDate,
    reservationStatus,
    usesVehicleRec,
}: any) {
    const { data: nirovisionAccessList } = useGetNirovisionAccessListQuery(
        bookingId,
        { skip: !bookingId },
    );

    const loggedInUser = UserRole();
    const canViewRequests = canViewNirovisionAccessRequests(loggedInUser?.role);
    const canRespondToRequests = canRespondToNirovisionAccessRequests(
        loggedInUser?.role,
    );
    const {
        data: accessRequests = [],
        isSuccess: accessRequestsReady,
        isError: accessRequestsFailed,
    } = useGetNirovisionAccessRequestsQuery(undefined, {
        skip: !canViewRequests || !bookingId,
    });
    const requestCount = accessRequests.filter(
        (request) => request.bookingId === bookingId,
    ).length;
    const showRequestsTab = canViewRequests && requestCount > 0;

    const [provideBookingAccess] = useProvideNirovisionAccessMutation();
    const [removeBookingAccess] = useRemoveNirovisionAccessMutation();

    const isMobile = useMediaQuery('(max-width: 770px)');

    const [tab, setTab] = useState<TabKey>(initialTab ?? 'without');
    const [query, setQuery] = useState('');

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const hasAppliedOpenTab = useRef(false);

    const { withList, withoutList, withAccessCount, withoutAccessCount } =
        useMemo(() => {
            const without = nirovisionAccessList?.faceRecNotProvidedTo ?? [];
            const withA = nirovisionAccessList?.faceRecProvidedTo ?? [];

            const withoutList = Array.isArray(without) ? without : [];
            const withList = Array.isArray(withA) ? withA : [];

            return {
                withList,
                withoutList,
                withAccessCount: withList.length,
                withoutAccessCount: withoutList.length,
            };
        }, [nirovisionAccessList]);

    const fallbackTab: TabKey = withAccessCount > 0 ? 'with' : 'without';
    const resolvedTab: TabKey =
        tab === 'requests' && !showRequestsTab ? fallbackTab : tab;

    useEffect(() => {
        if (!open) {
            hasAppliedOpenTab.current = false;
            return;
        }

        if (hasAppliedOpenTab.current) return;

        if (initialTab === 'requests') {
            setTab('requests');
            hasAppliedOpenTab.current = true;
            return;
        }

        if (initialTab) {
            setTab(initialTab);
            hasAppliedOpenTab.current = true;
            return;
        }

        // Wait for first load so we don't lock onto "without" before counts
        // are known. Later grant/remove refetches must not re-select a tab.
        if (!nirovisionAccessList) return;
        if (canViewRequests && !accessRequestsReady && !accessRequestsFailed) {
            return;
        }

        if (showRequestsTab) {
            setTab('requests');
        } else if (withAccessCount > 0) {
            setTab('with');
        } else {
            setTab('without');
        }
        hasAppliedOpenTab.current = true;
    }, [
        open,
        initialTab,
        nirovisionAccessList,
        canViewRequests,
        accessRequestsReady,
        accessRequestsFailed,
        showRequestsTab,
        withAccessCount,
    ]);

    useEffect(() => {
        if (!open) {
            setQuery('');
            setTab(initialTab ?? 'without');
        }
    }, [open, initialTab]);

    const mutateAccess = async (
        fn: (args: { bookingId: any; userId: number }) => any,
        userId: number,
        successMsg: string,
        failFallback: string,
    ): Promise<AccessResult> => {
        try {
            await fn({ bookingId, userId }).unwrap();
            toast.success(successMsg);
            return { ok: true };
        } catch (error) {
            const msg = handleHttpError(error) || failFallback;
            toast.error(msg);
            return { ok: false, error: msg };
        }
    };

    const provideAccess = (userId: number) =>
        mutateAccess(
            provideBookingAccess as any,
            userId,
            'Nirovision access granted successfully',
            'Failed to grant access',
        );

    const removeAccess = (userId: number) =>
        mutateAccess(
            removeBookingAccess as any,
            userId,
            'Nirovision access removed successfully',
            'Failed to remove access',
        );

    const matchesQuery = (row: any, q: string) => {
        const s = String(q || '')
            .trim()
            .toLowerCase();
        if (!s) return true;

        const name = `${row?.firstName ?? ''} ${row?.lastName ?? ''}`
            .trim()
            .toLowerCase();
        const email = String(row?.email ?? '')
            .trim()
            .toLowerCase();

        return name.includes(s) || email.includes(s);
    };

    const filteredWithout = useMemo(
        () => (withoutList || []).filter((r) => matchesQuery(r, query)),
        [withoutList, query],
    );

    const filteredWith = useMemo(
        () => (withList || []).filter((r) => matchesQuery(r, query)),
        [withList, query],
    );

    const showingCount =
        resolvedTab === 'requests'
            ? requestCount
            : resolvedTab === 'without'
              ? filteredWithout.length
              : filteredWith.length;
    const totalCount =
        resolvedTab === 'requests'
            ? requestCount
            : resolvedTab === 'without'
              ? withoutList.length
              : withList.length;

    const dataForView =
        resolvedTab === 'without'
            ? {
                  ...nirovisionAccessList,
                  faceRecNotProvidedTo: filteredWithout,
                  faceRecProvidedTo: [],
              }
            : {
                  ...nirovisionAccessList,
                  faceRecNotProvidedTo: [],
                  faceRecProvidedTo: filteredWith,
              };

    const handleTabChange = (_: any, v: TabKey) => {
        setTab(v);
        requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        });
    };

    const tz = 'Australia/Sydney';
    const now = moment.tz(tz);

    const startDate = moment.tz(reservationStartDate, tz).startOf('day');
    const endDate = moment.tz(reservationEndDate, tz).endOf('day');

    const accessWindowStart = startDate
        .clone()
        .subtract(1, 'day')
        .hour(15)
        .minute(0)
        .second(0)
        .millisecond(0);

    const csvEscape = (v: any) => {
        const s = String(v ?? '');
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
    };

    const formatDate = (d: any) => {
        if (!d) return '';
        const m = moment(d);
        return m.isValid() ? m.format('D MMM YYYY') : '';
    };

    const getAccessLabel = (
        row: any,
        type: 'face' | 'vehicle',
    ): 'Granted' | 'Not granted' | 'Revoked' => {
        const removedAt =
            type === 'face' ? row?.faceRecRemovedAt : row?.vehicleRemovedAt;
        if (removedAt) return 'Revoked';

        const providedAt =
            type === 'face' ? row?.faceRecProvidedAt : row?.vehicleProvidedAt;

        return providedAt ? 'Granted' : 'Not granted';
    };

    const isVehicleMissing = (row: any) =>
        usesVehicleRec && String(row?.vehiclePlate ?? '').trim()?.length === 0;

    const getStatusMessage = (row: any) => {
        if (!row?.isActive) return 'User is deactivated';

        const pic = row?.profilePicture;
        if (!pic?.id) return 'Profile photo required';

        const invalidFlag =
            pic?.isValid === false ||
            pic?.status === 'invalid' ||
            pic?.status === 'rejected';

        if (invalidFlag) return 'Photo needs review';

        const createdAt = pic?.createdAt || pic?.created || pic?.updatedAt;
        if (createdAt) {
            const picTime = moment(createdAt);
            if (picTime.isValid() && now.diff(picTime, 'hours') < 24) {
                return 'Photo recently updated (may take a moment to sync)';
            }
        }

        return null;
    };

    const buildDetailsText = (row: any) => {
        const lines: string[] = [];

        const info = getStatusMessage(row);
        if (info) lines.push(info);

        const faceProvided =
            row?.faceRecProvidedAt !== null && !row?.faceRecRemovedAt;
        const faceRevoked = Boolean(row?.faceRecRemovedAt);

        lines.push(
            `Face: ${
                faceRevoked
                    ? `Revoked (${moment(row.faceRecRemovedAt).format('D MMM YYYY')})`
                    : faceProvided
                      ? `Granted (${moment(row.faceRecProvidedAt).format('D MMM YYYY')})`
                      : 'Not granted'
            }`,
        );

        if (usesVehicleRec) {
            const vehicleProvided =
                row?.vehicleProvidedAt !== null && !row?.vehicleRemovedAt;
            const vehicleRevoked = Boolean(row?.vehicleRemovedAt);

            lines.push(
                `Vehicle: ${
                    vehicleRevoked
                        ? `Revoked (${moment(row.vehicleRemovedAt).format('D MMM YYYY')})`
                        : vehicleProvided
                          ? `Granted (${moment(row.vehicleProvidedAt).format('D MMM YYYY')})`
                          : 'Not granted'
                }`,
            );
        }

        if (isVehicleMissing(row)) {
            lines.push(
                'Rego not provided (granting access will apply to Face only)',
            );
        }

        if (String(reservationStatus) === 'pending') {
            lines.push(
                'Access is available once this booking is approved, and from 3pm the day before check-in.',
            );
        } else if (now.isBefore(accessWindowStart)) {
            lines.push(
                `Access starts ${accessWindowStart.format('D MMM, h:mm A')}`,
            );
        }

        return lines.join(' | ');
    };

    const buildExportRows = (
        rows: any[],
        group: 'With access' | 'Without access',
    ) =>
        (rows || []).map((row) => {
            const name =
                `${row?.firstName ?? ''} ${row?.lastName ?? ''}`.trim() ||
                'External Guest';

            return {
                group,
                name,
                email: row?.email ?? '',
                vehiclePlate: row?.vehiclePlate ?? '',
                faceAccess: getAccessLabel(row, 'face'),
                vehicleAccess: usesVehicleRec
                    ? getAccessLabel(row, 'vehicle')
                    : '',
                faceGrantedAt: formatDate(row?.faceRecProvidedAt),
                faceRevokedAt: formatDate(row?.faceRecRemovedAt),
                vehicleGrantedAt: usesVehicleRec
                    ? formatDate(row?.vehicleProvidedAt)
                    : '',
                vehicleRevokedAt: usesVehicleRec
                    ? formatDate(row?.vehicleRemovedAt)
                    : '',
                isActive: row?.isActive === false ? 'No' : 'Yes',
                details: buildDetailsText(row),
            };
        });

    const downloadCsv = (filename: string, rows: Record<string, any>[]) => {
        const headers = [
            'Group',
            'Name',
            'Email',
            'Vehicle Plate',
            'Face Access',
            'Vehicle Access',
            'Face Granted At',
            'Face Revoked At',
            'Vehicle Granted At',
            'Vehicle Revoked At',
            'Active',
            'Details',
        ];

        const lines = [
            headers.join(','),
            ...rows.map((r) =>
                [
                    r.group,
                    r.name,
                    r.email,
                    r.vehiclePlate,
                    r.faceAccess,
                    r.vehicleAccess,
                    r.faceGrantedAt,
                    r.faceRevokedAt,
                    r.vehicleGrantedAt,
                    r.vehicleRevokedAt,
                    r.isActive,
                    r.details,
                ]
                    .map(csvEscape)
                    .join(','),
            ),
        ];

        const blob = new Blob([lines.join('\n')], {
            type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();

        URL.revokeObjectURL(url);
    };

    const exportCsv = () => {
        const withRows = Array.isArray(nirovisionAccessList?.faceRecProvidedTo)
            ? nirovisionAccessList?.faceRecProvidedTo
            : [];
        const withoutRows = Array.isArray(
            nirovisionAccessList?.faceRecNotProvidedTo,
        )
            ? nirovisionAccessList?.faceRecNotProvidedTo
            : [];

        const exportRows = [
            ...buildExportRows(withRows, 'With access'),
            ...buildExportRows(withoutRows, 'Without access'),
        ];

        const from = moment(reservationStartDate).format('YYYYMMDD');
        const to = moment(reservationEndDate).format('YYYYMMDD');
        const filename = `nirovision_access_booking_${from}_${to}.csv`;

        downloadCsv(filename, exportRows);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth={false}
            PaperProps={{
                sx: isMobile
                    ? undefined
                    : {
                          width: '100%',
                          maxWidth: 1200,
                          maxHeight: 'calc(100dvh - 24px)',
                      },
            }}
        >
            <DialogTitle>
                Nirovision Access
                <IconButton
                    onClick={onClose}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Box
                sx={{
                    px: isMobile ? 2 : 3,
                    pb: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    flexShrink: 0,
                    backgroundColor: theme.palette.neutral.main,
                    borderBottom: `1px solid ${theme.palette.custom.lightBorderColor}`,
                }}
            >
                <DialogContentText
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: '14px',
                    }}
                >
                    {`Access starts from 3pm the day before check-in${removeAccessChecked ? ' and is revoked on checkout' : ''}.`}
                </DialogContentText>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1.25,
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Tabs
                        value={resolvedTab}
                        onChange={handleTabChange}
                        sx={{ flex: 1, minWidth: 280 }}
                        variant='standard'
                    >
                        <Tab
                            value='with'
                            label={`Users with access (${withAccessCount})`}
                            sx={{ textTransform: 'none', fontWeight: 900 }}
                        />
                        <Tab
                            value='without'
                            label={`Users without access (${withoutAccessCount})`}
                            sx={{ textTransform: 'none', fontWeight: 900 }}
                        />
                        {showRequestsTab ? (
                            <Tab
                                value='requests'
                                label={`Access requests (${requestCount})`}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 900,
                                }}
                            />
                        ) : null}
                    </Tabs>

                    {resolvedTab !== 'requests' ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                width: isMobile ? '100%' : 'auto',
                            }}
                        >
                            <TextField
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder='Search name or email'
                                size='small'
                                sx={{ width: isMobile ? '100%' : 380 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <SearchIcon
                                                fontSize='small'
                                                sx={{
                                                    color: theme.palette.text
                                                        .secondary,
                                                }}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {!isMobile && (
                                <Button
                                    onClick={exportCsv}
                                    startIcon={
                                        <FileDownloadOutlinedIcon
                                            sx={{ fontSize: 18 }}
                                        />
                                    }
                                    variant='outlined'
                                >
                                    Export
                                </Button>
                            )}
                        </Box>
                    ) : null}
                </Box>
            </Box>
            <DialogContent ref={scrollRef}>
                {resolvedTab === 'requests' ? (
                    <NirovisionAccessRequestsTab
                        bookingId={bookingId}
                        reservationStatus={reservationStatus}
                        actionsEnabled={canRespondToRequests}
                    />
                ) : (
                    <NirovisionAccessTable
                        accessType='booking'
                        bookerType={bookerType}
                        data={dataForView}
                        status={reservationStatus}
                        start={reservationStartDate}
                        end={reservationEndDate}
                        usesVehicleRec={usesVehicleRec}
                        provideAccess={provideAccess}
                        removeAccess={removeAccess}
                        forceMobile={isMobile}
                    />
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: isMobile ? 2 : 3,
                    py: 2,
                    justifyContent: 'space-between',
                }}
            >
                <Typography
                    variant='body3'
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 800,
                    }}
                >
                    Showing {showingCount} of {totalCount}
                </Typography>
                <Button variant='contained' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}
